import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { Info } from "lucide-react";
import Swal from 'sweetalert2';
import { fetchTalentDashboardApi, followUserApi, fetchPublicRoleListApi } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import ModernAuditionCard from "../../../components/ModernAuditionCard";

import TalentDashboardHeader from "./components/TalentDashboardHeader";
import TalentKpiGrid from "./components/TalentKpiGrid";
import TalentAuditionOverviewChart from "./components/TalentAuditionOverviewChart";
import TalentRecentApplicationsTable from "./components/TalentRecentApplicationsTable";
import FollowedCastingDirectorsModal from "./components/FollowedCastingDirectorsModal";

import "./TalentDashboard.css";

const TalentDashboard = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formatCamelCase = (text) => {
    if (!text) return "Casting Director";
    return String(text).replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ").replace(/\s+/g, " ").trim().replace(/\b\w/g, char => char.toUpperCase());
  };

  const navigateToCDProfile = (cdId) => {
    setShowFollowingModal(false);
    navigate(`/dashboard/casting-director-profile/${cdId}`);
  };

  const handleModalFollowToggle = async (cdId, cdName) => {
    try {
      const res = await followUserApi(cdId);
      if (res.data?.success || res.success) {
        setDashboardData((prev) => {
          if (!prev) return prev;
          let updatedList = (prev.followedCDs || []).filter(item => item.id !== cdId);
          return { ...prev, kpis: { ...prev.kpis, followingCount: updatedList.length }, followedCDs: updatedList };
        });
        Swal.fire({ icon: 'success', title: 'Unfollowed', text: `You have unfollowed ${cdName}.`, width: '300px', background: '#131824', color: '#ffffff', timer: 1500, showConfirmButton: false });
      }
    } catch (err) {
      console.error("Follow toggle error:", err);
    }
  };

  const { user } = useAuth();

  const fetchDashboard = async () => {
    try {
      const response = await fetchTalentDashboardApi();
      let data = response.data?.data || response.data;

      // If recommendations is empty, dynamically match from public active roles using candidate talent + gender
      if (!data?.recommendations || data.recommendations.length === 0) {
        try {
          const publicRes = await fetchPublicRoleListApi();
          const pubRoles = publicRes.data?.data || [];

          const userGender = String(user?.gender || '').trim().toLowerCase().replace(/[^a-z]/g, '');
          const userTalents = String(user?.talents || user?.talent || '').toLowerCase()
            .split(/[,;|/\s]+/)
            .map(t => t.trim().replace(/[^a-z0-9]/g, ''))
            .filter(Boolean);

          const matched = pubRoles.filter(r => {
            const rGender = String(r.gender || '').toLowerCase();
            const rTalent = String(r.roleType || r.roleCategory || '').toLowerCase();

            const genderOk = !rGender || !userGender || ['any', 'all', 'both', 'open'].some(k => rGender.includes(k)) || rGender.includes(userGender) || userGender.includes(rGender);
            const talentOk = !rTalent || userTalents.length === 0 || ['any', 'all', 'open'].some(k => rTalent.includes(k)) || userTalents.some(ut => ut.includes(rTalent) || rTalent.includes(ut));

            return genderOk && talentOk;
          });

          const chosenRoles = (matched.length > 0 ? matched : pubRoles).slice(0, 6);
          const finalRecs = chosenRoles.map(r => ({
            id: r.id,
            jogetRoleId: r.jogetRoleId || r.id,
            urlSlug: r.urlSlug || r.projectName,
            project: r.projectName,
            desc: r.roleTitle,
            category: r.roleCategory,
            img: r.projectImageUrl,
            types: r.auditionType,
            startDate: r.projectStartDate,
            endDate: r.projectEndDate,
            price: r.roleCost?.totalAmount ? `₹${r.roleCost.totalAmount}` : '₹0',
            isApplied: false
          }));

          if (finalRecs.length > 0) {
            data = { ...data, recommendations: finalRecs };
          }
        } catch (e) {
          console.error("Public roles matching fallback error:", e);
        }
      }

      setDashboardData(data);
    } catch (error) { 
      console.log(error); 
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => { 
    fetchDashboard(); 
  }, [user]);

  const getStatusBadge = (status) => {
    const s = status.toLowerCase();
    let color = "#9da3b0", bgColor = "rgba(157, 163, 176, 0.1)", text = status;
    if (s === "applied" || s === "pending") { color = "#ffb020"; bgColor = "rgba(255, 176, 32, 0.12)"; text = "Pending"; }
    else if (s === "reviewed") { color = "#06b6d4"; bgColor = "rgba(6, 182, 212, 0.12)"; text = "Reviewed"; }
    else if (s === "scheduled") { color = "#3b82f6"; bgColor = "rgba(59, 130, 246, 0.12)"; text = "Scheduled"; }
    else if (s === "selected" || s === "shortlisted") { color = "#00c781"; bgColor = "rgba(0, 199, 129, 0.12)"; text = "Shortlisted"; }
    else if (s === "rejected" || s === "resubmit") { color = "#ff4d4f"; bgColor = "rgba(255, 77, 79, 0.12)"; text = s === "resubmit" ? "Resubmit" : "Rejected"; }
    return <span className="fw-bold px-3 py-1.5" style={{ fontSize: '0.75rem', borderRadius: '4px', letterSpacing: '0.5px', color, backgroundColor: bgColor, display: 'inline-block', border: `1px solid ${color}26` }}>{text.toUpperCase()}</span>;
  };

  const COLOR_MAP = { Shortlisted: "#00c781", Pending: "#ffb020", Rejected: "#ff4d4f", Applications: "#E10600" };

  if (loading) {
    return (
      <div className="text-center p-5 text-white" style={{ minHeight: "80vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <Spinner animation="border" variant="danger" />
        <p className="mt-3">Loading Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="td-wrapper">
      <div className="td-container">
        <TalentDashboardHeader
          dashboardData={dashboardData}
          setShowFollowingModal={setShowFollowingModal}
          navigate={navigate}
        />

        <TalentKpiGrid
          dashboardData={dashboardData}
        />

        <div className="td-middle-grid">
          <TalentAuditionOverviewChart
            dashboardData={dashboardData}
            isMobile={isMobile}
            COLOR_MAP={COLOR_MAP}
          />

          <div className="td-recommended-wrapper">
            <div className="card-header td-rec-header">
              <h2>Recommended for You</h2>
              {dashboardData?.recommendations?.length > 2 && (<button className="td-view-all-btn" onClick={() => navigate("/roles")}>View All</button>)}
            </div>
            <div className="td-recommended-slider">
              {dashboardData?.recommendations?.length > 0 ? (
                dashboardData.recommendations.map((rec, index) => (
                  <div className="td-recommended-slide" key={index}>
                    <ModernAuditionCard 
                      audition={{ 
                        id: rec.id, 
                        jogetRoleId: rec.jogetRoleId || rec.id, 
                        urlSlug: rec.urlSlug || rec.project, 
                        projectName: rec.project, 
                        roleTitle: rec.desc, 
                        roleCategory: rec.category, 
                        projectImageUrl: rec.img, 
                        auditionType: Array.isArray(rec?.types) ? rec.types.join(';') : (rec?.types || "Online"), 
                        projectEndDate: rec.endDate, 
                        projectStartDate: rec.startDate,
                        isApplied: rec.isApplied,
                        hasApplied: rec.isApplied,
                        roleCost: { totalAmount: rec.price?.replace("₹", "")?.trim() } 
                      }} 
                    />
                  </div>
                ))
              ) : (
                <div className="w-100 text-center py-5 text-muted border border-secondary border-dashed rounded" style={{ borderStyle: 'dashed' }}>
                  <Info className="mb-2" size={24} /><p className="mb-0 small">No roles matching your profile gender/talent categories found yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <TalentRecentApplicationsTable
          dashboardData={dashboardData}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          navigate={navigate}
          getStatusBadge={getStatusBadge}
        />
      </div>

      <FollowedCastingDirectorsModal
        showFollowingModal={showFollowingModal}
        setShowFollowingModal={setShowFollowingModal}
        dashboardData={dashboardData}
        navigateToCDProfile={navigateToCDProfile}
        handleModalFollowToggle={handleModalFollowToggle}
        formatCamelCase={formatCamelCase}
      />
    </div>
  );
};

export default TalentDashboard;